import {Box, Button, TextField} from '@mui/material'
import {styled} from '@mui/material/styles'
import ErrorBox from '@ui/error/ErrorBox'
import LoadingBoundary from '@ui/loader'
import SignInContainer from '@ui/sign-in-container/SignInContainer'
// dispatch(push) removed - using navigate
import {Form, Formik} from 'formik'
import {CircleCheck} from 'lucide-react'
import React, {useState} from 'react'
import {NavLink, useLocation, useNavigate, useParams} from 'react-router-dom'
import * as Yup from 'yup'
import PasswordResetForm from '~/src/components/auth/PasswordResetForm'
import {BASE_ROUTES} from '~/src/routes/constants'
import {
  useConfirmPasswordResetMutation,
  useInitiatePasswordResetMutation,
  useTemporaryPasswordResetMutation,
} from '~/src/store/apis/auth-api'

const FormTitle = styled('h2')`
  font-size: 24px;
  font-weight: 400;
  margin-top: 50px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  gap: 10px;
`

const FormSubtitle = styled('p')`
  font-size: 14px;
  color: #666;
  margin-bottom: 32px;
`

const FieldContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
`

const BackLink = styled(NavLink)`
  color: #03a9fc;
  text-decoration: none;
  font-size: 14px;
  margin-top: 16px;
  display: inline-block;

  &:hover {
    text-decoration: underline;
  }
`

interface LocationState {
  email?: string
  token?: string // For temporary password reset from email
}

const errorText = {
  sameAsLast3Passwords: 'New password cannot match previous 3 passwords',
}

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{email?: string; confirmationCode?: string}>()
  const locationState = location.state as LocationState | undefined

  // Two scenarios for password reset:
  // 1. Coming from email with tempEmail and token (BASE_ROUTES.PASSWORD_RESET) -> use temporaryPasswordReset
  // 2. User clicks "Forgot Password" from sign-in with email/confirmationCode -> use confirmPasswordReset
  const hasTemporaryToken = !!(locationState?.email && locationState.token)
  const hasConfirmationCode = !!(params.email && params.confirmationCode)
  const directToForgotPassword = hasTemporaryToken || hasConfirmationCode

  const [email, setEmail] = useState(locationState?.email ?? '')
  const [isLoading, setIsLoading] = useState(false)
  const [customMessage, setCustomMessage] = useState('')

  // Add countdown timer state (from SHR-FE)
  const [countDown, setCountDown] = useState(60)
  const [activeCounter, setActiveCounter] = useState(false)

  const [initiateReset, {error: initiateError}] = useInitiatePasswordResetMutation()
  const [confirmReset, {error: confirmError}] = useConfirmPasswordResetMutation()
  const [temporaryReset, {error: temporaryError}] = useTemporaryPasswordResetMutation()

  // Add countdown timer effect (from SHR-FE)
  React.useEffect(() => {
    if (activeCounter && countDown > 1) {
      const timer = countDown > 0 && setInterval(() => setCountDown(countDown - 1), 1000)
      return () => clearInterval(timer as NodeJS.Timeout)
    }
    if (activeCounter && countDown === 1) {
      setActiveCounter(false)
      setCountDown(60)
    }
  }, [countDown, activeCounter])

  const emailValidationSchema = Yup.object({
    email: Yup.string().email('Please enter a valid email address').required('Email is required'),
  })

  // SHR-FE handleSubmit for email step
  const handleSubmit = async (values: {email: string}) => {
    setCustomMessage('')
    try {
      setIsLoading(true)
      await initiateReset({email: values.email}).unwrap()
      setEmail(values.email)
      setActiveCounter(true) // Start countdown timer
    } catch (error: unknown) {
      console.error('Failed to initiate password reset:', error)
      const err = error as {data?: {message?: string}}
      // Handle specific error messages like SHR-FE
      if (err.data?.message?.includes('Document not found')) {
        setCustomMessage('Incorrect email')
        setTimeout(() => setCustomMessage(''), 3000)
      } else {
        setCustomMessage('Failed to send reset email. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // SHR-FE handlePasswordResetSubmit for password reset step
  const handlePasswordResetSubmit = async (values: {password: string; confirm: string}) => {
    setCustomMessage('')
    try {
      setIsLoading(true)

      // Scenario 1: Temporary password reset from email link with token
      if (hasTemporaryToken && locationState) {
        await temporaryReset({
          email: locationState.email!,
          token: locationState.token!,
          newPassword: values.password,
        }).unwrap()
      }
      // Scenario 2: Confirm password reset with verification code
      else if (hasConfirmationCode) {
        await confirmReset({
          email: params.email,
          verificationCode: params.confirmationCode,
          newPassword: values.password,
        }).unwrap()
      }

      // Navigate to password confirmation like SHR-FE
      navigate(BASE_ROUTES.PASSWORD_CONFIRMATION, {
        state: {
          password: values.password,
          email: hasTemporaryToken ? locationState.email : params.email,
        },
      })
    } catch (error: unknown) {
      const err = error as {data?: {message?: string}}
      if (err.data?.message?.includes('New password cannot match previous 3 passwords')) {
        setCustomMessage(errorText.sameAsLast3Passwords)
        setTimeout(() => setCustomMessage(''), 5000)
      } else {
        setCustomMessage('Failed to reset password. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToSignIn = () => {
    navigate(BASE_ROUTES.SIGNIN)
  }

  const renderEmailStep = () => (
    <Formik
      initialValues={{email}}
      validationSchema={emailValidationSchema}
      onSubmit={handleSubmit}>
      {({values, handleChange, handleBlur, errors, touched, isSubmitting}) => (
        <Form>
          <FormTitle>
            {activeCounter ? (
              <>
                <CircleCheck style={{color: '#69C659'}} /> Email Sent
              </>
            ) : (
              <>Reset Your Password</>
            )}
          </FormTitle>
          <FormSubtitle>
            {activeCounter
              ? `We sent a reset link to ${email}. Check your email and follow the instructions.`
              : 'Enter the login email address associated with your account and we will send you a link to reset your password!'}
          </FormSubtitle>

          {customMessage && (
            <Box sx={{mb: 2}}>
              <ErrorBox>{customMessage}</ErrorBox>
            </Box>
          )}

          {initiateError && (
            <Box sx={{mb: 2}}>
              <ErrorBox>Failed to send reset email. Please try again.</ErrorBox>
            </Box>
          )}

          <>
            <FieldContainer>
              <TextField
                name="email"
                label="Email"
                type="email"
                value={values.email}
                onChange={(e) => {
                  handleChange(e)
                  setEmail(e.target.value)
                }}
                onBlur={handleBlur}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                fullWidth
                variant="outlined"
                InputLabelProps={{shrink: true}}
              />
            </FieldContainer>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting || (activeCounter && countDown > 0)}
              sx={{mb: 2}}>
              {activeCounter ? `Send link to email (${countDown})` : 'Send link to email'}
            </Button>
          </>
        </Form>
      )}
    </Formik>
  )

  // SHR-FE displayPasswordSwitch equivalent
  const renderContent = () => {
    if (directToForgotPassword) {
      // Show PasswordResetForm when we have email and confirmationCode in URL OR temp token
      return (
        <>
          <FormTitle>{hasTemporaryToken ? 'Set Password' : 'Reset Password'}</FormTitle>
          {hasTemporaryToken && locationState?.email && (
            <FormSubtitle style={{marginTop: 0}}>Login Email: {locationState.email}</FormSubtitle>
          )}
          <PasswordResetForm
            isError={!!(confirmError ?? temporaryError)}
            customMessage={customMessage}
            onSubmit={handlePasswordResetSubmit}
          />
        </>
      )
    } else {
      // Show email form (SendEmailForm equivalent)
      return renderEmailStep()
    }
  }

  return (
    <LoadingBoundary isLoading={isLoading}>
      <SignInContainer
        customHeader="Password Reset"
        customFooter={
          directToForgotPassword ? null : (
            <NavLink to={BASE_ROUTES.SIGNIN}>
              <span>
                Remember your password? <u>Sign In</u>
              </span>
            </NavLink>
          )
        }>
        {renderContent()}
      </SignInContainer>
    </LoadingBoundary>
  )
}

export default ForgotPassword
